import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { PublicLayout } from '@/components/PublicLayout';

// Public pages
import HomePage from '@/pages/HomePage';
import ServicesHub from '@/pages/ServicesHub';
import ServicePage from '@/pages/ServicePage';
import AboutPage from '@/pages/AboutPage';
import TransformationsPage from '@/pages/TransformationsPage';
import ReviewsPage from '@/pages/ReviewsPage';
import FacilityPage from '@/pages/FacilityPage';
import ContactPage from '@/pages/ContactPage';
import LocationsHub from '@/pages/LocationsHub';
import LocationPage from '@/pages/LocationPage';
import ThankYouPage from '@/pages/ThankYouPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { PrivacyPage, TermsPage, AccessibilityPage } from '@/pages/LegalPages';

// Admin (lazy)
const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminLeads = lazy(() => import('@/pages/admin/AdminLeads'));
const AdminLeadDetail = lazy(() => import('@/pages/admin/AdminLeadDetail'));
const AdminMedia = lazy(() => import('@/pages/admin/AdminMedia'));
const AdminSettingsHub = lazy(() => import('@/pages/admin/AdminSettingsHub'));
const AdminEmailSettings = lazy(() => import('@/pages/admin/AdminEmailSettings'));
const AdminSecuritySettings = lazy(() => import('@/pages/admin/AdminSecuritySettings'));
const AdminAuditLog = lazy(() => import('@/pages/admin/AdminAuditLog'));

const Loading = () => (
  <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
    <div className="text-[color:var(--ft-muted)]">Loading…</div>
  </div>
);

// Redirect legacy paths
function RedirectTo({ to }) { return <Navigate to={to} replace />; }

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Admin routes (no public layout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="leads/:id" element={<AdminLeadDetail />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="settings" element={<AdminSettingsHub />} />
            <Route path="settings/email" element={<AdminEmailSettings />} />
            <Route path="settings/security" element={<AdminSecuritySettings />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
          </Route>

          {/* Public routes */}
          <Route path="/*" element={
            <PublicLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesHub />} />
                <Route path="/services/:slug" element={<ServicePage />} />
                <Route path="/about-jarad-wingard" element={<AboutPage />} />
                <Route path="/about" element={<RedirectTo to="/about-jarad-wingard" />} />
                <Route path="/our-team" element={<RedirectTo to="/about-jarad-wingard" />} />
                <Route path="/client-transformations" element={<TransformationsPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/client-reviews" element={<RedirectTo to="/reviews" />} />
                <Route path="/facility" element={<FacilityPage />} />
                <Route path="/gallery-2" element={<RedirectTo to="/facility" />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/contact-1" element={<RedirectTo to="/contact" />} />
                <Route path="/locations" element={<LocationsHub />} />
                <Route path="/locations/:slug" element={<LocationPage />} />
                <Route path="/personal-trainer-stuart-fl" element={<RedirectTo to="/" />} />
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route path="/privacy-policy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/accessibility" element={<AccessibilityPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </PublicLayout>
          } />
        </Routes>
      </Suspense>
      <Toaster richColors closeButton position="top-right" theme="dark" />
    </BrowserRouter>
  );
}
